import "../scss/styles.scss";
import Swiper from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Navigation, Pagination, Autoplay } from "swiper/modules";

// --------------------
// Helper function to toggle active classes
// --------------------
const toggleActiveClasses = (element, isActive) => {
  const link = element.querySelector(".nav-link");
  const icon = element.querySelector(".navbar__icon");

  if (!link || !icon) return;

  if (isActive) {
    element.classList.add("active");
    link.classList.add("active");
    icon.classList.add("active");
  } else {
    element.classList.remove("active");
    link.classList.remove("active");
    icon.classList.remove("active");
  }
};

// --------------------
// Navbar
// --------------------
document.addEventListener("DOMContentLoaded", () => {
  const navItems = document.querySelectorAll(".nav-item");
  const sections = document.querySelectorAll("section");
  const links = document.querySelectorAll(".nav-link");
  const navbarCollapse = document.getElementById("navbarNav");
  const navbarIcon = document.getElementById("navbarIcon");

  const updateActiveLink = () => {
    sections.forEach((section) => {
      const sectionTop = section.offsetTop;
      const sectionHeight = section.clientHeight;
      const sectionId = section.getAttribute("id");

      if (
        window.scrollY >= sectionTop - sectionHeight / 3 &&
        window.scrollY < sectionTop + sectionHeight
      ) {
        navItems.forEach((navItem) => {
          toggleActiveClasses(
            navItem,
            navItem.querySelector(".nav-link")?.getAttribute("href") === `#${sectionId}`
          );
        });
      }
    });
  };

  const handleNavItemClick = (event) => {
    navItems.forEach((item) => toggleActiveClasses(item, false));
    toggleActiveClasses(event.currentTarget, true);
  };

  const toggleMenu = () => {
    if (!navbarCollapse || !navbarIcon) return;
    navbarCollapse.classList.toggle("show");
    const isMenuOpen = navbarCollapse.classList.contains("show");
    navbarIcon.textContent = isMenuOpen ? "close" : "menu";
  };

  links.forEach((link) => {
    link.addEventListener("click", (e) => {
      e.preventDefault();
      const targetId = link.getAttribute("href");
      const targetSection = document.querySelector(targetId);
      if (targetSection) {
        targetSection.scrollIntoView({ behavior: "smooth", block: "start" });
        if (navbarCollapse?.classList.contains("show")) {
          navbarCollapse.classList.remove("show");
          navbarIcon.textContent = "menu";
        }
      }
    });
  });

  document.addEventListener("scroll", updateActiveLink);
  navItems.forEach((navItem) =>
    navItem.addEventListener("click", handleNavItemClick)
  );
  window.toggleMenu = toggleMenu;
});

// --------------------
// Copy to clipboard
// --------------------
window.copyToClipboard = function (text, buttonElement) {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      const tooltip = buttonElement.querySelector(".tooltip");
      if (!tooltip) return;
      tooltip.textContent = "Skopiowano do schowka";
      setTimeout(() => {
        tooltip.textContent = "Skopiuj";
      }, 2000);
    })
    .catch(() => console.error("Nie udało się skopiować tekstu"));
};

// --------------------
// Language
// --------------------
async function loadLanguage(lang) {
  try {
    const res = await fetch(`assets/lang/${lang}.json`);
    if (!res.ok) throw new Error(`Nie udało się załadować pliku assets/lang/${lang}.json`);
    const translations = await res.json();

    document.querySelectorAll("[data-i18n]").forEach((el) => {
      const key = el.getAttribute("data-i18n");
      const value = key.split(".").reduce((obj, k) => obj?.[k], translations);
      if (value) el.innerHTML = value;
    });
  } catch (error) {
    console.error("Błąd ładowania tłumaczeń:", error);
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const languageSwitcher = document.getElementById("languageSwitcher");
  if (!languageSwitcher) return;
  const defaultLang = languageSwitcher.value;
  loadLanguage(defaultLang);

  languageSwitcher.addEventListener("change", (event) => {
    loadLanguage(event.target.value);
  });
});

// --------------------
// Swiper
// --------------------
document.addEventListener("DOMContentLoaded", () => {
  const swiperEl = document.querySelector(".app-screenshots__swiper");
  if (!swiperEl) return;

  new Swiper(swiperEl, {
    modules: [Navigation, Pagination, Autoplay],
    loop: true,
    spaceBetween: 30,
    autoplay: { delay: 4000, pauseOnMouseEnter: true },
    pagination: { el: ".app-screenshots__pagination", clickable: true, dynamicBullets: true },
    navigation: {
      nextEl: ".app-screenshots__button-next",
      prevEl: ".app-screenshots__button-prev",
    },
    breakpoints: {
      320: { spaceBetween: 15 },
      640: { spaceBetween: 20 },
      768: { spaceBetween: 25 },
      1024: { spaceBetween: 30 },
    },
  });
});

document.addEventListener("DOMContentLoaded", () => {
  const guidesModal = document.getElementById("guidesModal");
  const modalDocuments = document.getElementById("modalDocuments");
  const guideButtons = document.querySelectorAll(".how-it-works__guide-link");
  const closeButton = guidesModal.querySelector(".how-it-works__modal-close");

  // Public endpoint for fetching tutorials
  // In development mode we use proxy through webpack-dev-server (avoid CORS)
  // In production directly to API
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const API_ENDPOINT = window.API_ENDPOINT || 
    (isLocalhost 
      ? "/api/v1/tutorials"  // Proxy through webpack-dev-server
      : "https://panel.concile.online/api/v1/tutorials");  // Directly in production

  // Variable to store scroll position
  let savedScrollY = 0;

  // Opening modal
  guideButtons.forEach(btn => {
    btn.addEventListener("click", async () => {
      const language = btn.dataset.language || "pl";
      
      // Save current scroll position
      savedScrollY = window.scrollY || window.pageYOffset;
      
      // Disable body scrolling
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${savedScrollY}px`;
      document.body.style.width = "100%";
      
      // Open modal
      guidesModal.classList.add("show");

      modalDocuments.innerHTML = `
        <div class="how-it-works__modal-loading">
          <div class="how-it-works__modal-spinner" role="status" aria-label="Ładowanie">
            <span style="position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); border: 0;">Ładowanie...</span>
          </div>
        </div>
      `;

      try {
        const tutorials = await fetchTutorials(language);
        renderTutorials(tutorials);
      } catch (error) {
        console.error("Error fetching tutorials:", error);
        modalDocuments.innerHTML = `<div class="how-it-works__modal-error">Błąd ładowania tutoriali. Spróbuj ponownie później.</div>`;
      }
    });
  });

  // Closing modal
  closeButton.addEventListener("click", closeModal);
  guidesModal.addEventListener("click", e => {
    if (e.target === guidesModal) closeModal();
  });

  function closeModal() {
    guidesModal.classList.remove("show");
    
    // Restore body scrolling
    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";
    
    // Restore scroll position with small delay so styles reset first
    requestAnimationFrame(() => {
      window.scrollTo(0, savedScrollY);
      savedScrollY = 0;
    });
  }

  /**
   * Fetches tutorials for selected language from public endpoint
   * @param {string} language - Language code (e.g. 'pl', 'en', 'ru', 'de', 'uk')
   * @param {string|null} category - Optional category for filtering
   * @returns {Promise<Array>} Array of tutorials
   */
  async function fetchTutorials(language, category = null) {
    let url = `${API_ENDPOINT}/${language}`;
    if (category) {
      url += `?category=${encodeURIComponent(category)}`;
    }
    
    try {
      const response = await fetch(url);
      
      if (!response.ok) {
        // If 404, endpoint probably doesn't exist
        if (response.status === 404) {
          throw new Error(`Endpoint nie został znaleziony (404). Sprawdź czy API jest dostępne pod adresem: ${url}`);
        }
        
        // Attempt to parse error response
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `Błąd HTTP: status ${response.status}`);
      }
      
      const data = await response.json();
      
      // Sprawdzenie struktury odpowiedzi API
      if (!data.success) {
        throw new Error(data.error?.message || "Błąd pobierania tutoriali");
      }
      
      return data.data?.tutorials || [];
    } catch (error) {
      // If it's a network/CORS error, add more detailed information
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        throw new Error(
          `Nie można połączyć się z API. ` +
          `Problem może być po stronie serwera (CORS lub niedostępność endpointu). ` +
          `URL: ${url}`
        );
      }
      throw error;
    }
  }

  function escapeHtml(text) {
    if (!text) return "";
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Renders list of tutorials in modal
   * @param {Array} tutorials - Array of tutorial objects
   */
  function renderTutorials(tutorials) {
    if (!tutorials || tutorials.length === 0) {
      modalDocuments.innerHTML = `<div class="how-it-works__modal-empty">No tutorials available</div>`;
      return;
    }

    // Sort by sort_order if available
    const sortedTutorials = [...tutorials].sort((a, b) => {
      const orderA = a.sort_order || 0;
      const orderB = b.sort_order || 0;
      return orderA - orderB;
    });

    modalDocuments.innerHTML = sortedTutorials.map(tutorial => `
      <div class="how-it-works__modal-document-item">
        <button type="button" class="how-it-works__modal-document-btn"
          data-tutorial-url="${escapeHtml(tutorial.file_url || "")}"
          data-tutorial-name="${escapeHtml(tutorial.file_name || tutorial.title)}"
          ${!tutorial.file_url ? 'disabled' : ''}>
          <span class="material-symbols-outlined how-it-works__modal-document-icon">description</span>
          <div class="how-it-works__modal-document-info">
            <span class="how-it-works__modal-document-name">${escapeHtml(tutorial.title)}</span>
            ${tutorial.description ? `<span class="how-it-works__modal-document-description">${escapeHtml(tutorial.description)}</span>` : ''}
            ${tutorial.file_name ? `<span class="how-it-works__modal-document-filename">${escapeHtml(tutorial.file_name)}</span>` : ''}
          </div>
          ${tutorial.file_url ? '<span class="material-symbols-outlined how-it-works__modal-document-download">download</span>' : ''}
        </button>
      </div>
    `).join("");

    // Add click handler for tutorial buttons
    modalDocuments.querySelectorAll(".how-it-works__modal-document-btn:not([disabled])").forEach(btn => {
      btn.addEventListener("click", async () => {
        const url = btn.dataset.tutorialUrl;
        const name = btn.dataset.tutorialName;
        
        if (!url) return;
        
        try {
          // Attempt to download file as blob
          const response = await fetch(url);
          if (!response.ok) throw new Error();
          
          const blob = await response.blob();
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(link.href);
        } catch (error) {
          // Fallback: open in new tab
          console.error("Error downloading file:", error);
          window.open(url, "_blank", "noopener,noreferrer");
        }
      });
    });
  }
});

